import { createI18n, type I18nOptions } from 'vue-i18n';

import { nextTick } from 'vue';

import { usePreferredLanguages } from '@vueuse/core';

const langs = ['en', 'de', 'sk'] as const;
const languages = usePreferredLanguages();
const guessedLanguage = languages.value.find(
  (elem) => elem.length === 2 && langs.includes(elem as AVAILABLE_LANGUAGES_TYPE),
);
export const DEFAULT_LANGUAGE = guessedLanguage || 'en';
export const AVAILABLE_LANGUAGES = langs;
export type AVAILABLE_LANGUAGES_TYPE = (typeof AVAILABLE_LANGUAGES)[number];

export type LanguageMapping<T> = {
  [K in (typeof langs)[number]]: T;
};

// we need to initialize the messages object with each available language
// in order to have them present in the language selector
const initMessages: { [key: string]: any } = {};
AVAILABLE_LANGUAGES.forEach((lang) => {
  initMessages[lang] = {};
});

const i18n = createI18n({
  legacy: false,
  availableLocales: AVAILABLE_LANGUAGES,
  fallbackLocale: DEFAULT_LANGUAGE,
  locale: DEFAULT_LANGUAGE,
  messages: initMessages as I18nOptions['messages'],
  missingWarn: false,
  fallbackWarn: false,
});

const loadedLanguages: string[] = [];

export async function initLocalization() {
  const { switchLanguage, loadLocaleMessages } = useLocalization();
  for (const l of i18n.global.availableLocales) {
    await loadLocaleMessages(l);
  }
  return switchLanguage(DEFAULT_LANGUAGE);
}

export function useLocalization() {
  const loadLocaleMessages = async (locale: string) => {
    // load locale messages with dynamic import
    const messages = await import(`./locales/${locale}.json`);

    // set locale and locale message
    i18n.global.setLocaleMessage(locale, messages.default);

    return nextTick();
  };
  const switchLanguage = async (lang: string) => {
    if (!loadedLanguages.includes(lang)) {
      await loadLocaleMessages(lang);
      loadedLanguages.push(lang);
    }
    i18n.global.locale.value = lang;
    document.querySelector('html')?.setAttribute('lang', lang);
    return Promise.resolve();
  };

  return { i18n, t: i18n.global.t, locale: i18n.global.locale, switchLanguage, loadLocaleMessages };
}
